import { ApiError, getLocationTariff, upsertLocationTariff } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { TariffResponse, UpsertTariffRequest } from '@jq4race/shared';
import { Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import type { TariffFormValues } from '../components/TariffForm';
import { TariffForm } from '../components/TariffForm';

// ── Cents ↔ Dollars conversion helpers (form boundary only) ───────────────

/** Convert integer cents to a dollars number for display. Null-safe. */
function centsToDisplay(cents: number | null | undefined): number | null {
  if (cents === null || cents === undefined) return null;
  return cents / 100;
}

/** Convert API response to TariffFormValues (dollars for monetary fields). */
function responseToFormValues(tariff: TariffResponse): Partial<TariffFormValues> {
  return {
    mode: tariff.mode,
    differentiatedByDay: tariff.differentiatedByDay,
    amountCents: centsToDisplay(tariff.amountCents),
    dailyCapCents: centsToDisplay(tariff.dailyCapCents),
    weekdayAmountCents: centsToDisplay(tariff.weekdayAmountCents),
    weekendAmountCents: centsToDisplay(tariff.weekendAmountCents),
    // Hotel nightly rate fields (JQ4-17 · US-CONFIG-003)
    guestAmountCents: centsToDisplay(tariff.guestAmountCents),
    nonGuestAmountCents: centsToDisplay(tariff.nonGuestAmountCents),
    // Delay-recharge grace period, in minutes (post-payment-delay-recharge · S2)
    // — already an integer, no cents conversion needed.
    graceMinutes: tariff.graceMinutes,
  };
}

// ── Component ─────────────────────────────────────────────────────────────

export function TariffPage() {
  const { t } = useTranslation('location-tariffs');
  const { locationId } = useParams({ strict: false });

  const [initialValues, setInitialValues] = useState<Partial<TariffFormValues> | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const tariff = await getLocationTariff(locationId);
      setInitialValues(responseToFormValues(tariff));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // No tariff configured yet — show empty form (Scenario 24).
        setInitialValues(undefined);
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
      }
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner.
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  async function handleSubmit(values: UpsertTariffRequest) {
    if (!locationId) return;
    // values arriving here have monetary fields already in INTEGER CENTS
    // (converted by TariffForm's setValueAs). Pass directly to the API.
    try {
      const saved = await upsertLocationTariff(locationId, values);
      setInitialValues(responseToFormValues(saved));
      setSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        throw new LocalizedError(translateApiError(e, t, { fallback: t('feedback.saveError') }));
      }
      throw e;
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <TariffForm
              onSubmit={handleSubmit}
              defaultValues={initialValues}
              submitLabel={t('form.saveTariff')}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-tariffs');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
